/* Pack prices belong to Shopify variants. The page never invents a cart discount. */
(function (global) {
  'use strict';
  const colours = ['Green', 'White', 'Black'];
  const money = pence => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
  function key(names) {
    if (![1, 2, 4].includes(names.length) || names.some(name => !colours.includes(name))) throw new Error('Choose a colour for every bottle.');
    return [...names].sort().join(' + ');
  }
  function resolve(catalog, names) {
    const wanted = key(names);
    const variant = catalog.variants.find(item => Number.parseInt(item.option1, 10) === names.length && (item.option2 || item.option1.split(' — ')[1]) === wanted);
    if (!variant) throw new Error('This colour combination is not available. Choose another pack.');
    if (!Number.isSafeInteger(variant.price) || variant.price <= 0) throw new Error('Please refresh the page to check the price.');
    return variant;
  }
  function accessory(catalog, kind, quantity) {
    const label = kind === 'kit' ? 'The accessory kit' : 'Panda Baby';
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > 4) throw new Error('Choose an accessory quantity from 0 to 4.');
    if (!quantity) return null;
    const variant = catalog[kind]?.variants.find(item => Number.parseInt(item.option1, 10) === quantity);
    if (!variant || !Number.isSafeInteger(variant.price) || variant.price <= 0) throw new Error(label + ' is not available in this quantity.');
    return variant;
  }
  function quote(catalog, names, kitQuantity = 0, charmQuantity = 0) {
    const variant = resolve(catalog, names);
    if (kitQuantity > names.length || charmQuantity > names.length) throw new Error('Choose up to one of each accessory per bottle.');
    const kit = accessory(catalog, 'kit', kitQuantity);
    const charm = accessory(catalog, 'charm', charmQuantity);
    const subtotal = variant.price + (kit?.price || 0) + (charm?.price || 0);
    const shipping = subtotal >= catalog.shippingThreshold ? 0 : catalog.shippingPrice;
    const freeDeliveryAt = catalog.shippingThreshold;
    return { variant, kit, charm, kitQuantity, charmQuantity, subtotal, shipping, total: subtotal + shipping, freeDeliveryAt, remaining: Math.max(0, freeDeliveryAt - subtotal) };
  }
  function payload(catalog, names, kitQuantity = 0, charmQuantity = 0) {
    if (catalog.preview || !catalog.enabled || catalog.currency !== 'GBP') throw new Error('This is a preview. Checkout is not enabled.');
    const q = quote(catalog, names, kitQuantity, charmQuantity);
    const items = [];
    for (const [variant, label] of [[q.variant, 'This bottle pack'], [q.kit, 'The accessory kit'], [q.charm, 'Panda Baby']]) {
      if (!variant) continue;
      if (!variant.available || !/^\d+$/.test(String(variant.id))) throw new Error(label + ' is currently unavailable. Choose another quantity or remove it.');
      items.push({ id: variant.id, quantity: 1 });
    }
    return { items };
  }
  function quantityAfterPackChange(quantity, nextPack, customised) {
    return customised ? Math.min(quantity, nextPack) : nextPack;
  }
  function summary(bottles, kits, charms) {
    return bottles + (bottles === 1 ? ' bottle' : ' bottles') + (kits ? ' + ' + kits + (kits === 1 ? ' kit' : ' kits') : '') + (charms ? ' + ' + charms + (charms === 1 ? ' Panda Baby' : ' Panda Babies') : '');
  }
  function initialSelection(catalog, search) {
    const params = new URLSearchParams(search);
    const variant = catalog.variants.find(item => String(item.id) === params.get('variant'));
    if (variant) {
      const names = (variant.option2 || variant.option1.split(' — ')[1] || '').split(' + ');
      if (names.length === Number.parseInt(variant.option1, 10) && [1, 2, 4].includes(names.length) && names.every(name => colours.includes(name))) {
        return { names, kit: false, charm: false };
      }
    }
    // Campaign names identify the advertised pack; prices always come from Shopify.
    const concept = [params.get('utm_content'), params.get('utm_term')]
      .map(value => /^NN_UK_(?:PANDA_)?(C01_SCHOOL|C03_DUO|C04_GIFT|C05_PERSONALISE)(?:_|$)/i.exec(value || '')?.[1]?.toUpperCase())
      .find(Boolean);
    const complete = concept === 'C05_PERSONALISE';
    return { names: concept === 'C03_DUO' ? ['Green', 'White'] : ['Green'], kit: complete, charm: complete };
  }
  const api = { colours, money, key, resolve, accessory, quote, payload, quantityAfterPackChange, summary, initialSelection };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!global.document) return;

  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const data = JSON.parse(root.querySelector('[data-catalog]').textContent);
    const form = root.querySelector('[data-pack-form]');
    const selects = [...root.querySelectorAll('[data-colour]')];
    const kitInput = root.querySelector('[data-kit]');
    const charmInput = root.querySelector('[data-charm]');
    const charmDialog = root.querySelector('[data-charm-dialog]');

    let charmOffered = false, offering = false;
    const pickerPanel = root.querySelector('[data-picker-panel]');
    const packCards = [...root.querySelectorAll('[data-pack-card]')];
    const messages = root.querySelectorAll('[data-message]');
    const cartLinks = root.querySelectorAll('[data-review-cart]');
    const submit = root.querySelector('[data-add]');
    const buyNow = root.querySelector('[data-buy-now]');
    const buttons = [submit, buyNow, root.querySelector('[data-sticky-add]')];
    const nativeCheckout = root.querySelector('[data-native-checkout]');
    const sticky = root.querySelector('[data-sticky]');
    const mainImage = root.querySelector('[data-main-image]');
    const packContents = root.querySelector('[data-pack-contents]');
    const packDelivery = root.querySelector('[data-pack-delivery]');

    const initial = initialSelection(data, global.location.search);
    form.querySelector('[name="pack"][value="' + initial.names.length + '"]').checked = true;
    initial.names.forEach((name, i) => { selects[i].value = name; });
    kitInput.checked = initial.kit;
    charmInput.checked = initial.charm;
    charmOffered = initial.charm;
    showImage(initial.charm ? 'Complete' : initial.names[0]);

    let busy = false;
    const count = () => Number(form.elements.pack.value);
    const selection = () => selects.slice(0, count()).map(select => select.value);
    let previousPack = count();
    const quantities = { kit: count(), charm: count() };
    const customised = { kit: false, charm: false };
    let offerQuantity = count();
    const kitCount = () => kitInput.checked ? quantities.kit : 0;
    const charmCount = () => charmInput.checked ? quantities.charm : 0;
    const available = variant => !!variant && (data.preview || (variant.available && /^\d+$/.test(String(variant.id))));
    function refreshStepper(kind, quantity) {
      root.querySelector('[data-' + kind + '-count]').textContent = quantity;
      root.querySelectorAll('[data-quantity="' + kind + '"]').forEach(button => {
        button.disabled = busy || (Number(button.dataset.step) < 0 ? quantity <= 1 : quantity >= count());
      });
    }
    function refreshOffer() {
      const q = quote(data, selection(), kitCount(), offerQuantity);
      const label = offerQuantity + (offerQuantity === 1 ? ' Panda Baby' : ' Panda Babies');
      root.querySelector('[data-charm-offer-price]').textContent = money(q.charm.price);
      root.querySelector('[data-charm-offer-count]').textContent = label + ' · optional';
      root.querySelector('[data-charm-offer-label]').textContent = 'Add ' + label + ' · ' + money(q.charm.price);
      root.querySelector('[data-charm-offer-add]').disabled = !available(q.charm);
      root.querySelector('[data-charm-summary]').textContent = summary(count(), kitCount(), offerQuantity);
      root.querySelector('[data-charm-total]').textContent = money(q.total) + ' total';
      root.querySelector('[data-charm-delivery]').textContent = q.shipping ? money(q.shipping) + ' delivery included' : 'Free UK delivery included';
      refreshStepper('offer', offerQuantity);
      root.querySelector('[data-offer-controls]').hidden = count() === 1;
    }
    function feedback(text, review = false) {
      messages.forEach(message => { message.textContent = text; });
      cartLinks.forEach(link => { link.hidden = !review; });
    }
    function showImage(colour) {
      const button = root.querySelector('[data-gallery="' + colour + '"]');
      if (!button) return;
      mainImage.src = button.dataset.image;
      mainImage.alt = button.dataset.alt || colour + ' NiuNiu Panda Bottle with carry handle and shoulder strap';
      root.querySelectorAll('[data-gallery]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      root.querySelector('[data-gallery-colour]').textContent = button.dataset.caption || colour;
    }
    function update() {
      feedback('');
      if (previousPack !== count()) {
        for (const kind of ['kit', 'charm']) quantities[kind] = quantityAfterPackChange(quantities[kind], count(), customised[kind]);
        previousPack = count();
      }
      const activeCard = packCards.find(card => Number(card.dataset.packCard) === count());
      if (pickerPanel.parentElement !== activeCard) activeCard.append(pickerPanel);
      packCards.forEach(card => card.classList.toggle('is-selected', card === activeCard));
      selects.forEach((select, i) => {
        const row = select.closest('[data-colour-row]');
        row.hidden = i >= count(); select.disabled = i >= count();
        row.querySelector('[data-colour-image]').src = root.querySelector('[data-gallery="' + select.value + '"]').dataset.image;
      });
      root.querySelector('[data-colour-summary]').textContent = selection().join(' + ');
      try {
        const q = quote(data, selection(), kitCount(), charmCount());
        root.querySelector('[data-pack-price]').textContent = money(q.subtotal);
        if (packContents) packContents.textContent = summary(count(), kitCount(), charmCount());
        if (packDelivery) packDelivery.textContent = q.shipping ? money(q.shipping) + ' UK delivery · Free on orders £32+' : 'Free UK delivery';
        root.querySelector('[data-add-price]').textContent = money(q.subtotal);
        root.querySelector('[data-sticky-price]').textContent = money(q.subtotal);
        const orderSummary = summary(count(), kitCount(), charmCount());
        root.querySelector('[data-sticky-summary]').textContent = orderSummary;
        root.querySelector('[data-order-summary]').textContent = orderSummary;
        for (const kind of ['kit', 'charm']) {
          let variant;
          try { variant = accessory(data, kind, quantities[kind]); } catch (_) { variant = null; }
          const input = kind === 'kit' ? kitInput : charmInput;
          const label = quantities[kind] + (kind === 'kit' ? (quantities[kind] === 1 ? ' Decorate & Clean Kit' : ' Decorate & Clean Kits') : (quantities[kind] === 1 ? ' Panda Baby' : ' Panda Babies'));
          root.querySelector('[data-' + kind + '-label]').textContent = 'Add ' + label;
          root.querySelector('[data-' + kind + '-price]').textContent = variant ? money(variant.price) : 'Unavailable';
          root.querySelectorAll('[data-open-' + kind + '-text]').forEach(element => { element.textContent = variant ? 'Add ' + label + ' · ' + money(variant.price) : label + ' · unavailable'; });
          input.disabled = busy || (!input.checked && !available(variant));
          root.querySelector('[data-' + kind + '-controls]').hidden = count() === 1 || (kind === 'charm' && !input.checked);
          root.querySelectorAll('[data-open-' + kind + ']').forEach(button => { button.disabled = busy || (kind === 'kit' ? !available(variant) : !data.charm?.variants.some(available)); });
          refreshStepper(kind, quantities[kind]);
        }
        root.querySelector('[data-kit-includes]').hidden = !kitInput.checked;
        root.querySelector('[data-kit-includes]').textContent = quantities.kit * 10 + ' assorted stickers + ' + quantities.kit + (quantities.kit === 1 ? ' bottle brush + 1 straw brush.' : ' bottle brushes + ' + quantities.kit + ' straw brushes.');
        root.querySelector('[data-charm-selection]').hidden = !charmInput.checked;
        root.querySelector('[data-charm-teaser]').hidden = charmInput.checked;
        root.querySelector('[data-sticky-image]').src = root.querySelector('[data-gallery="' + selects[0].value + '"]').dataset.image;
        root.querySelector('[data-shipping-message]').textContent = q.remaining ? 'Add ' + money(q.remaining) + ' for free UK delivery' : 'Free delivery unlocked';
        const progress = root.querySelector('[data-shipping-progress]');
        progress.max = q.freeDeliveryAt;
        progress.value = Math.min(q.subtotal, q.freeDeliveryAt);
        root.querySelector('[data-shipping-status]').classList.toggle('is-unlocked', !q.remaining);
        root.querySelector('[data-delivery-note]').textContent = q.shipping ? 'Otherwise ' + money(q.shipping) + ' delivery' : '';
        const unavailable = data.currency !== 'GBP' || !available(q.variant) || (q.kit && !available(q.kit)) || (q.charm && !available(q.charm));
        form.inert = busy;
        buttons.forEach(button => {
          button.disabled = busy || unavailable;
          button.querySelector('[data-add-label]').textContent = busy ? 'Please wait…' : unavailable ? 'Currently unavailable' : button === buyNow ? 'Shop now' : 'Add to cart';
        });
        if (nativeCheckout) {
          try {
            const { items } = payload(data, selection(), kitCount(), charmCount());
            const variantInput = nativeCheckout.querySelector('[data-express-variant]');
            const kitVariant = nativeCheckout.querySelector('[data-express-kit]');
            const kitQuantity = nativeCheckout.querySelector('[data-express-kit-quantity]');
            kitVariant.value = q.kit ? q.kit.id : '';
            kitQuantity.value = '1';
            kitVariant.disabled = kitQuantity.disabled = !kitInput.checked;
            const charmVariant = nativeCheckout.querySelector('[data-express-charm]');
            const charmQuantity = nativeCheckout.querySelector('[data-express-charm-quantity]');
            charmVariant.value = q.charm ? q.charm.id : '';
            charmQuantity.value = '1';
            charmVariant.name = 'shopify-additional-items[' + (kitInput.checked ? 2 : 1) + '][id]';
            charmQuantity.name = 'shopify-additional-items[' + (kitInput.checked ? 2 : 1) + '][quantity]';
            charmVariant.disabled = charmQuantity.disabled = !charmInput.checked;
            variantInput.value = items[0].id;
            variantInput.disabled = false;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
            nativeCheckout.hidden = false; nativeCheckout.inert = busy; buyNow.hidden = true;
          } catch (_) { nativeCheckout.hidden = true; buyNow.hidden = false; }
        }
      } catch (error) {
        root.querySelector('[data-pack-price]').textContent = '—';
        if (packContents) packContents.textContent = 'Check your selection';
        if (packDelivery) packDelivery.textContent = '';
        feedback(error.message); buttons.forEach(button => { button.disabled = true; });
        if (nativeCheckout) nativeCheckout.hidden = true;
        buyNow.hidden = false;
      }
    }
    form.addEventListener('change', event => {
      update();
      if (event.target.matches('[data-colour]')) showImage(event.target.value);
      if (event.target === charmInput && !charmInput.checked) root.querySelector('[data-charm-teaser]').focus({ preventScroll: true });
    });
    root.querySelectorAll('[data-gallery]').forEach(button => button.addEventListener('click', () => {
      if (colours.includes(button.dataset.gallery)) selects[0].value = button.dataset.gallery;
      showImage(button.dataset.gallery); update();
    }));
    root.querySelectorAll('[data-choose-duo]').forEach(button => button.addEventListener('click', () => {
      form.querySelector('[value="2"]').checked = true; update();
      form.scrollIntoView({ behavior: global.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      form.querySelector('[value="2"]').focus({ preventScroll: true });
    }));
    root.querySelectorAll('[data-open-kit]').forEach(button => button.addEventListener('click', () => {
      kitInput.checked = true; update(); kitInput.scrollIntoView({ block: 'center' }); kitInput.focus({ preventScroll: true });
    }));
    function offerCharm(continuing) {
      if (offering || busy) return Promise.resolve();
      if (!data.charm?.variants.some(available)) return Promise.resolve();
      offerQuantity = quantities.charm;
      refreshOffer();
      root.querySelector('[data-charm-skip]').textContent = continuing ? 'No thanks, continue' : 'Not now';
      charmOffered = true; offering = true;
      charmDialog.returnValue = '';
      return new Promise(resolveOffer => {
        charmDialog.addEventListener('close', () => {
          offering = false;
          if (charmDialog.returnValue === 'add') {
            quantities.charm = offerQuantity;
            customised.charm = offerQuantity !== count();
            charmInput.checked = true;
            update();
            charmInput.focus({ preventScroll: true });
          }
          resolveOffer();
        }, { once: true });
        charmDialog.showModal();
      });
    }
    root.querySelectorAll('[data-quantity]').forEach(button => button.addEventListener('click', () => {
      if (busy) return;
      const kind = button.dataset.quantity;
      const next = Math.max(1, Math.min(count(), (kind === 'offer' ? offerQuantity : quantities[kind]) + Number(button.dataset.step)));
      if (kind === 'offer') { offerQuantity = next; refreshOffer(); }
      else { quantities[kind] = next; customised[kind] = next !== count(); update(); }
    }));
    root.querySelectorAll('[data-open-charm]').forEach(button => button.addEventListener('click', () => offerCharm(false)));
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy || offering) return;
      const checkout = event.submitter === buyNow;
      feedback('');
      try {
        if (!checkout && !charmInput.checked && !charmOffered) await offerCharm(true);
      } catch (error) { feedback(error.message); return; }
      if (data.preview || !data.enabled) {
        try {
          const q = quote(data, selection(), kitCount(), charmCount());
          feedback('Preview: ' + summary(count(), kitCount(), charmCount()) + ' (' + selection().join(', ') + '). ' + money(q.total) + ' including UK delivery. No order placed.');
        } catch (error) { feedback(error.message); }
        return;
      }
      let body;
      try { body = payload(data, selection(), kitCount(), charmCount()); }
      catch (error) { feedback(error.message); return; }
      busy = true; update();
      try {
        const drawer = checkout ? null : document.querySelector('cart-drawer');
        if (drawer) {
          body.sections = drawer.getSectionsToRender().map(section => section.id);
          body.sections_url = global.location.pathname;
          drawer.setActiveElement(event.submitter);
        }
        const response = await fetch(data.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'The basket could not be updated.');
        if (drawer) {
          drawer.renderContents(result);
          busy = false; update();
        } else global.location.assign(checkout ? data.cartUrl.replace(/\/cart\/?$/, '/checkout') : data.cartUrl);
      } catch (error) {
        // A lost response may follow a successful add: never retry automatically.
        busy = false; update();
        feedback('We could not confirm the basket update. Review your basket before trying again.', true);
      }
    });
    update();
    const scrollEvents = new AbortController();
    let scrollQueued = false;
    function updateSticky() { sticky.hidden = submit.getBoundingClientRect().bottom >= 0; }
    global.addEventListener('scroll', () => {
      if (scrollQueued) return;
      scrollQueued = true;
      global.requestAnimationFrame(() => { scrollQueued = false; updateSticky(); });
    }, { passive: true, signal: scrollEvents.signal });
    global.addEventListener('resize', updateSticky, { signal: scrollEvents.signal });
    updateSticky();
    root.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => scrollEvents.abort(), { once: true });
    if ('IntersectionObserver' in global) {
      const revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('np-reveal-pending');
        revealObserver.unobserve(entry.target);
      }), { rootMargin: '0px 0px -24px 0px' });
      if (!global.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        root.querySelectorAll('[data-reveal]').forEach(element => {
          if (element.getBoundingClientRect().top < global.innerHeight) return;
          element.classList.add('np-reveal-pending');
          revealObserver.observe(element);
        });
      }
      root.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
        revealObserver.disconnect();
      }, { once: true });
    }
  }
  global.document.querySelectorAll('[data-panda-page]').forEach(init);
  global.document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-panda-page]').forEach(init));
})(typeof window !== 'undefined' ? window : globalThis);
